import { createRouter, createWebHistory } from 'vue-router';
import { setActiveRoute } from './utils/consoleFilter';
import { watch } from 'vue';
import { useAuthStore } from './stores/authStore';
import { useProfileStore } from './stores/profileStore';
import { useFeatureStore } from './stores/featureStore'; // Import Feature Store

// Lazy load components for better performance
const Login = () => import('./views/auth/Login.vue');
const Register = () => import('./views/auth/Register.vue');
const VerifyEmail = () => import('./views/auth/VerifyEmail.vue');
const Profile = () => import('./views/profile/Profile.vue');
const ForgotPassword = () => import('./views/auth/ForgotPassword.vue');
const TeacherDashboard = () => import('./views/dashboard/TeacherDashboard.vue');
const ManageStudents = () => import('./views/students/ManageStudents.vue');
const CreateNotebook = () => import('./views/notebooks/CreateNotebook.vue');
const MyNotebooks = () => import('./views/notebooks/MyNotebooks.vue');
const ExtractGrades = () => import('./views/grades/ExtractGrades.vue');
const GradeExtractionHistory = () => import('./views/grades/GradeExtractionHistory.vue');
const CurriculumManager = () => import('./views/curriculum/CurriculumManager.vue');
const AdminDashboard = () => import('./views/admin/AdminDashboard.vue');
const RechargeCredits = () => import('./views/payments/RechargeCredits.vue');
const PaymentSuccess = () => import('./views/payments/PaymentSuccess.vue');
const PaymentError = () => import('./views/payments/PaymentError.vue');
const ExamResultLanding = () => import('./views/public/ExamResultLanding.vue');
const HelpGuide = () => import('./views/public/HelpGuide.vue');
const LandingPage = () => import('./views/public/LandingPage.vue');
const PrivacyPolicy = () => import('./views/public/PrivacyPolicy.vue');
const TermsOfService = () => import('./views/public/TermsOfService.vue');

const AILessonPlanGenerator = () => import('./components/lesson-generator/LessonGeneratorMain.vue');
const TemplateManager = () => import('./components/template-builder/TemplateManager.vue');
const AITestGenerator = () => import('./views/ai-content/AITestGenerator.vue');
const AIExerciseGenerator = () => import('./views/ai-content/AIExerciseGenerator.vue');
const AIContentHistory = () => import('./views/ai-content/AIContentHistory.vue');
const GamesLobby = () => import('./views/games/GamesLobby.vue');
const GameViewer = () => import('./views/games/GameViewer.vue');

const routes = [
  {
    path: '/',
    name: 'Home',
    component: LandingPage,
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore();
      if (authStore.user) {
        next({ name: 'AILessonPlanGenerator' });
      } else {
        next();
      }
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { guestOnly: true },
  },
  {
    path: '/register',
    name: 'Register',
    component: Register,
    meta: { guestOnly: true },
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: VerifyEmail,
    meta: { requiresAuth: true },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { guestOnly: true },
  },
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard',
    name: 'TeacherDashboard',
    component: TeacherDashboard,
    meta: { requiresAuth: true, title: 'لوحة التحكم الرئيسية' },
  },
  {
    path: '/manage-students',
    name: 'ManageStudents',
    component: ManageStudents,
    meta: { requiresAuth: true, title: 'إدارة التلاميذ' },
  },
  {
    path: '/notebooks/create',
    name: 'CreateNotebook',
    component: CreateNotebook,
    meta: { requiresAuth: true, title: 'إنشاء دفتر الأنشطة' },
  },
  {
    path: '/notebooks',
    name: 'MyNotebooks',
    component: MyNotebooks,
    meta: { requiresAuth: true, title: 'دفاتري المحفوظة' },
  },
  {
    path: '/grades/extract',
    name: 'ExtractGrades',
    component: ExtractGrades,
    meta: { requiresAuth: true, title: 'استخراج النقاط' },
  },
  {
    path: '/grades',
    name: 'GradeExtractionHistory',
    component: GradeExtractionHistory,
    meta: { requiresAuth: true, title: 'سجل النقاط المستخرجة' },
  },
  {
    path: '/ai-content/templates',
    name: 'TemplateManager',
    component: TemplateManager,
    meta: { requiresAuth: true, title: 'قوالب المذكرات' },
  },
  {
    path: '/ai-content/lesson-plan',
    name: 'AILessonPlanGenerator',
    component: AILessonPlanGenerator,
    meta: { requiresAuth: true, title: 'مولد المذكرات (الذكاء الاصطناعي)' },
  },
  {
    path: '/ai-content/test',
    name: 'AITestGenerator',
    component: AITestGenerator,
    meta: { requiresAuth: true, title: 'مولد الاختبارات' },
  },
  {
    path: '/ai-content/exercise',
    name: 'AIExerciseGenerator',
    component: AIExerciseGenerator,
    meta: { requiresAuth: true, title: 'مولد التمارين' },
  },
  {
    path: '/ai-content',
    name: 'AIContentHistory',
    component: AIContentHistory,
    meta: { requiresAuth: true, title: 'سجل المحتوى المولد' },
  },
  {
    path: '/games',
    name: 'GamesLobby',
    component: GamesLobby,
    meta: { requiresAuth: true, title: 'الألعاب التعليمية' },
  },
  {
    path: '/curriculum-manager',
    name: 'CurriculumManager',
    component: CurriculumManager,
    meta: { requiresAuth: true, title: 'إدارة المناهج (مدير)' },
  },
  {
    path: '/recharge',
    name: 'RechargeCredits',
    component: RechargeCredits,
    meta: { requiresAuth: true, title: 'شحن الرصيد' },
  },
  {
    path: '/payment-success',
    name: 'PaymentSuccess',
    component: PaymentSuccess,
    meta: { requiresAuth: true },
  },
  {
    path: '/payment-failed',
    name: 'PaymentError',
    component: PaymentError,
    meta: { requiresAuth: true },
  },
  {
    path: '/exam-landing/:examId',
    name: 'ExamResultLanding',
    component: ExamResultLanding,
    meta: { guestOnly: false }, // Allow everyone
  },
  {
    path: '/help',
    name: 'HelpGuide',
    component: HelpGuide,
    meta: { requiresAuth: true },
  },
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy,
    meta: { guestOnly: false },
  },
  {
    path: '/terms',
    name: 'TermsOfService',
    component: TermsOfService,
    meta: { guestOnly: false },
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: AdminDashboard,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Navigation Guards
const waitForAuth = async () => {
  const authStore = useAuthStore();
  await authStore.authReady;
};

router.beforeEach(async (to, from, next) => {
  await waitForAuth();
  const authStore = useAuthStore();
  
  const requiresAuth = to.meta.requiresAuth;
  const guestOnly = to.meta.guestOnly;

  if (guestOnly && authStore.user) {
    return next({ name: 'AILessonPlanGenerator' });
  }

  if (requiresAuth && !authStore.user) {
    return next({ name: 'Login' });
  }

  // --- 🛡️ EMAIL VERIFICATION GATE ---
  if (authStore.user && !authStore.emailVerified) {
    const isExempt = ['VerifyEmail', 'Profile', 'PrivacyPolicy', 'TermsOfService'].includes(to.name);
    if (!isExempt) {
      return next({ name: 'VerifyEmail' });
    }
  }

  // Admin Check Logic
  if (to.meta.requiresAdmin) {
    const profileStore = useProfileStore();
    if (!profileStore.hasProfileData && authStore.user) {
      try { await profileStore.fetchProfile(); } catch (e) {}
    }
    if (profileStore.professionalProfile?.role !== 'admin') {
      return next({ name: 'AILessonPlanGenerator' });
    }
  }

  // --- Feature Flag Check ---
  const featureStore = useFeatureStore();
  if (!featureStore.isLoaded && authStore.user) {
    try { await featureStore.fetchFeatures(); } catch(e) {}
  }

  if (to.name) {
      const isAllowed = featureStore.canAccessFeature(to.name);
      if (!isAllowed && to.name !== 'AILessonPlanGenerator') {
          return next({ name: 'AILessonPlanGenerator' }); 
      }
  }

  next();
});

router.afterEach((to) => {
  setActiveRoute(to.name);
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
});

export default router;